import { AnyAction } from "redux";

import {
  SERVICE_LOCATION_BY_ID_ENDPOINT,
  SERVICE_LOCATIONS_ENDPOINT,
} from "@config/endpoints";

import { SERVICE_TYPE } from "@typings/globals";
import { ThunkActionDispatch } from "@typings/reduxThunkActions";

import { AsyncActionResult } from "@services/AsyncActionCreatorFactory";
import { ReduxActionNameCreator } from "@services/ReduxActionNameCreator";
import { buildUrl } from "@services/BuildUrl";
import { kebabToSnake } from "@services/KebabToSnake";

import { genericPendingAction } from "@app/services/GenericPendingAction";
import { asyncActionCreator } from "@app/services/AsyncActionCreator";
import { IStore } from "@app/redux/reducers";
import {
  setLocalStorageHelper,
  setFromLocalStorageHelper,
  deleteLocalStorageHelper,
} from "@app/redux/helpers";

import {
  LOCAL_STORAGE_KEY,
  ServiceLocationParams,
} from "./serviceLocation.typings";

const actionCreator = new ReduxActionNameCreator("serviceLocation", true);

export const FETCH_SERVICE_LOCATION: string = actionCreator.make(
  "fetch service location",
);
export const FETCH_SERVICE_LOCATION_COVERAGE = actionCreator.make(
  "fetch service location coverage",
);
export const SET_IN_LOCAL_STORAGE = actionCreator.make("set in local storage");
export const SET_FROM_LOCAL_STORAGE = actionCreator.make(
  "set from local storage",
);
export const DELETE_LOCAL_STORAGE = actionCreator.make("delete local storage");
export const RESET_STATE = actionCreator.make("reset state");

export const fetchServiceLocation =
  (serviceLocationId: string) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    const action = await asyncActionCreator({
      url: SERVICE_LOCATION_BY_ID_ENDPOINT.replace("{id}", serviceLocationId),
      action: FETCH_SERVICE_LOCATION,
    });

    return dispatch(action);
  };

/**
 * Checks if zip code is covered by our cleaning services.
 * When `serviceType` is undefined backend checks for both Home Cleanings and End of Tenancy.
 */
export const fetchServiceLocationCoverage =
  (zip: string, serviceType?: SERVICE_TYPE, params?: ServiceLocationParams) =>
  async (dispatch: ThunkActionDispatch): Promise<AsyncActionResult> => {
    dispatch(genericPendingAction(FETCH_SERVICE_LOCATION_COVERAGE));

    const serviceTypeQueryValue = serviceType
      ? kebabToSnake(serviceType).toUpperCase()
      : "";

    const action = await asyncActionCreator({
      url: buildUrl(SERVICE_LOCATIONS_ENDPOINT)
        .setQuery("zip", zip, { optional: false })
        .setQuery("serviceType", serviceTypeQueryValue, { optional: true })
        .done(),
      action: FETCH_SERVICE_LOCATION_COVERAGE,
      params,
    });

    return dispatch(action);
  };

export const setLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch, store: () => IStore): void => {
    setLocalStorageHelper(
      dispatch,
      SET_IN_LOCAL_STORAGE,
      store().serviceLocation,
      LOCAL_STORAGE_KEY,
    );
  };

export const setFromLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    setFromLocalStorageHelper(
      dispatch,
      SET_FROM_LOCAL_STORAGE,
      LOCAL_STORAGE_KEY,
    );
  };

export const deleteLocalStorage =
  () =>
  (dispatch: ThunkActionDispatch): void => {
    deleteLocalStorageHelper(dispatch, DELETE_LOCAL_STORAGE, LOCAL_STORAGE_KEY);
  };

export const resetState = (): AnyAction => ({
  type: RESET_STATE,
});
